/**
 * SIL-1348 - the Silicon Passport card on the buyer listing detail (spec
 * section 4.2, handoff section 2 item 1).
 *
 * THE RULE THIS FILE EXISTS FOR: **a CSS blur is a leak, not a redaction.**
 *
 * The unverified body renders `PASSPORT_PLACEHOLDER_ROWS` — fixed dummy
 * strings, identical for every listing and every viewer — under the blur, and
 * it NEVER receives the verified evidence in the first place: the component
 * switches on `evidence.tier` before rendering anything, so the real numbers
 * are not in the element tree to be recovered by disabling the filter, reading
 * the accessibility tree, or viewing source. (The server already withholds
 * them from an unverified caller — `projectEvidence` nulls the "full" fields at
 * the SQL layer — so this is the second of two boundaries, not the only one.)
 *
 * The verified body COMPOSES `VerifiedEvidenceBody` (SIL-1340) for the per-run
 * counts and the custody summary rather than restating those tables. This card
 * adds what SIL-1340 did not have: the header with the three-valued AV chip,
 * the pass-ratio headline with the explainer trigger, and the FMV band.
 */
import { FileLock2 } from 'lucide-react';
import type { UnverifiedEvidence, VerifiedEvidence } from '../../dto/marketplace.js';
import { listingPriceView, type ListingPriceView } from './listingPriceView.js';
import type { BuyerPricing } from './listingShape.js';
import { Card } from '../Card.js';
import { Button } from '../Button.js';
import { verdictFamilyFromWindowCounts } from '../../verification/presentation.js';
import { formatDate } from '../format.js';
import { VerifiedEvidenceBody } from './EvidencePanel.js';
import { ActiveVerificationExplainerTrigger } from './ActiveVerificationExplainerModal';
import { AvStateChip, AvStateNote } from './AvStateChip';
import { BUYER_VERIFY_BANNER_CTA } from './copy/buyerVerification';
import {
  AV_PASS_RATIO_CAPTION,
  PASSPORT_AV_HEADING,
  PASSPORT_CARD_EYEBROW,
  PASSPORT_FMV_HEADING,
  PASSPORT_FMV_HIGH,
  PASSPORT_FMV_LOW,
  PASSPORT_FMV_MEDIAN,
  PASSPORT_FMV_UNAVAILABLE,
  PASSPORT_LOCKED_BODY,
  PASSPORT_LOCKED_HEADING,
  PASSPORT_PLACEHOLDER_ROWS,
  avPassRatio,
  avRunMeta,
  fmvCompsLine,
  passportNodeCountLine,
  pctVsFmvLabel,
} from './copy/listingDetail';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function Eyebrow({ children }: { children: string }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{children}</p>
  );
}

// ---------------------------------------------------------------------------
// Verified body
// ---------------------------------------------------------------------------

function FmvBand({ view }: { view: ListingPriceView }) {
  const band = view.fmvBand;
  return (
    <div>
      <Eyebrow>{PASSPORT_FMV_HEADING}</Eyebrow>
      {band === null ? (
        <p className="mt-2 text-sm text-muted-foreground">{PASSPORT_FMV_UNAVAILABLE}</p>
      ) : (
        <>
          <dl className="mt-2 grid grid-cols-3 gap-x-4">
            {(
              [
                [PASSPORT_FMV_LOW, band.low],
                [PASSPORT_FMV_MEDIAN, band.median],
                [PASSPORT_FMV_HIGH, band.high],
              ] as const
            ).map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs text-muted-foreground">{label}</dt>
                <dd className="mt-0.5 font-num text-sm text-foreground">{usd.format(value)}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-2 text-xs text-muted-foreground">{fmvCompsLine(band.comps)}</p>
          {view.pctVsFmv !== null && (
            <p className="mt-1 text-xs text-foreground">
              {view.perGpu} · {pctVsFmvLabel(view.pctVsFmv)}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function VerifiedPassportBody({
  evidence,
  pricing,
}: {
  evidence: VerifiedEvidence;
  pricing: BuyerPricing;
}) {
  const view = listingPriceView(pricing, evidence);
  const vs = evidence.verificationSummary;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Eyebrow>{PASSPORT_AV_HEADING}</Eyebrow>
            <ActiveVerificationExplainerTrigger />
          </div>
          <p className="mt-2 font-num text-2xl font-semibold text-foreground">
            {avPassRatio(vs.pass, vs.nodes)}
          </p>
          <p className="text-xs text-muted-foreground">{AV_PASS_RATIO_CAPTION}</p>
          {evidence.avState === 'active' && (
            <p className="mt-2 text-xs text-muted-foreground">
              {avRunMeta(vs.tier, formatDate(vs.runAt), formatDate(vs.validTo))}
            </p>
          )}
          <AvStateNote avState={evidence.avState} className="mt-2" />
        </div>
        {/* An unverified PRICING with verified EVIDENCE is not a shape the API
            produces (both come off the same tier), but the union permits it —
            render no band rather than guess at one. */}
        {view.mode === 'verified' ? <FmvBand view={view} /> : null}
      </div>
      <VerifiedEvidenceBody evidence={evidence} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Unverified body — the blurred placeholder
// ---------------------------------------------------------------------------

function LockedPassportBody({ onGetVerified }: { onGetVerified: () => void }) {
  return (
    <div className="relative overflow-hidden rounded-lg">
      {/*
        `aria-hidden` and `select-none` on the placeholder: it is decoration
        standing in for content this viewer may not have. A screen reader hears
        the overlay's explanation instead, which is the actual message.
      */}
      <div
        aria-hidden="true"
        className="select-none space-y-3 p-4 opacity-55 blur-[5px]"
        data-testid="passport-locked-placeholder"
      >
        {PASSPORT_PLACEHOLDER_ROWS.map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-6">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">{row.label}</span>
            <span className="font-num text-sm text-foreground">{row.value}</span>
          </div>
        ))}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-card/40 p-4 text-center">
        <FileLock2 className="size-6 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm font-semibold text-foreground">{PASSPORT_LOCKED_HEADING}</p>
        <p className="max-w-sm text-xs text-muted-foreground">{PASSPORT_LOCKED_BODY}</p>
        <Button variant="secondary" size="sm" className="mt-1" onClick={onGetVerified}>
          {BUYER_VERIFY_BANNER_CTA}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

export interface PassportCardProps {
  evidence: VerifiedEvidence | UnverifiedEvidence;
  pricing: BuyerPricing;
  /** `listing.assetFingerprint` — the public passport handle. Null for a lot. */
  passportId: string | null;
  nodeCount: number;
  /** Opens the buyer-verification modal via `useVerificationGate`. */
  onGetVerified: () => void;
}

export function PassportCard({
  evidence,
  pricing,
  passportId,
  nodeCount,
  onGetVerified,
}: PassportCardProps) {
  const evidenceTier = evidence.tier === 'verified' ? evidence.verificationSummary.tier : null;
  // SIL-1371 — the window verdict gating the Verified mark. Same derivation, and
  // the same deliberate `null` for the unverified tier, as `ListingCard`; see the
  // long note there and `showsVerifiedMark`'s doc comment. Note the locked body
  // below renders fixed placeholder strings, so an unverified viewer has NOTHING
  // beside this chip to qualify a green shield — the reason the issue called this
  // surface the sharpest case.
  const windowVerdict =
    evidence.tier === 'verified' ? verdictFamilyFromWindowCounts(evidence.verificationSummary) : null;

  return (
    <Card as="section">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Eyebrow>{PASSPORT_CARD_EYEBROW}</Eyebrow>
          <p className="mt-1 truncate font-mono text-xs text-muted-foreground">
            {[passportId, passportNodeCountLine(nodeCount)].filter(Boolean).join(' · ')}
          </p>
        </div>
        <AvStateChip
          avState={evidence.avState}
          evidenceTier={evidenceTier}
          windowVerdict={windowVerdict}
        />
      </div>
      <div className="mt-4">
        {evidence.tier === 'verified' ? (
          <VerifiedPassportBody evidence={evidence} pricing={pricing} />
        ) : (
          <LockedPassportBody onGetVerified={onGetVerified} />
        )}
      </div>
    </Card>
  );
}
